import React, { useCallback, useMemo, useState } from 'react';
import '@patternfly/react-styles/css/utilities/Sizing/sizing.css';
import { useParams, useHistory } from 'react-router-dom';
import _ from 'lodash';

import { useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import useApiCall from 'services/useApiCall';
import useAPI from 'services/useAPI';
import useAlerts from 'utils/hooks/useAlerts';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import Tl from 'localization/LocalizationKeys';
import { useTranslation } from 'localization/useTranslation';
import { format } from 'utils/string';

import NewModelStep from './steps/NewModelStep';
import SelectAssetsStep from './steps/SelectAssetsStep';
import SelectBindingStep from './steps/SelectBindingStep';
import MigrationReviewStep from './steps/MigrationReviewStep';
import styles from './steps/steps.module.css';
import GlazeWizard from 'components/Wizard/GlazeWizard';
import { getUniqueContentBindingDescriptionIds } from '../utils/model';
import { assetKeys } from 'components/Admin/Assets/queries';
import { useBindingsByModel } from '../queries';
import { useAssets } from 'components/Asset/queries';
import { modelKeys } from '../queries';

const migrationRequest = (targetModelId, assetIds, bindingMap) => {
  const m = {
    targetAssetModelId: _.first(_.split(targetModelId, '.')),
    assetIds: assetIds
  };
  if (bindingMap) {
    m.bindingDescriptionIdMap = bindingMap;
  }
  return m;
};

const MigrateModel = ({ breadcrumbs }) => {
  const T = useTranslation();
  const history = useHistory();
  const { modelId, assetId } = useParams();
  const api = useAPI();
  const [migrationInfo, setMigrationInfo] = useState({
    targetModel: {},
    targetAssetIds: [assetId],
    sourceModel: {},
    currentBindingIds: [],
    currentDefaultBindingIds: []
  });

  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();


  const modelsQuery = useQuery(modelKeys.models(), createQueryRequest(api.core.getModels), {
    onSuccess: (data) => {
      if (modelId != null) {
        const targetModel = _.find(data, (m) => m.id === modelId);
        setMigrationInfo(oldInfo => ({
          ...oldInfo,
          targetModel: targetModel
        }));
      }
    }
  });
  const models = modelsQuery.data;

  const { assets } = useAssets();

  const baseModel = migrationInfo?.targetModel?.id;
  const { bindingsQuery } = useBindingsByModel(baseModel, false);

  useQuery(assetKeys.asset(assetId), createQueryRequest(api.core.getAsset), {
    enabled: (assetId != null) && (models != null),
    onSuccess: (asset) => {
      const sourceModel = _.find(models, (m) => _.first(_.split(m.id, '.')) === asset.modelId);
      const { assetBindingIds, defaultBindingIds } = getUniqueContentBindingDescriptionIds([asset]);
      setMigrationInfo(oldInfo => ({
        ...oldInfo,
        sourceModel: sourceModel,
        currentBindingIds: assetBindingIds,
        currentDefaultBindingIds: defaultBindingIds
      }));
    }
  });

  const [, targetBindings] = useMemo(() => {
    if (_.isEmpty(bindingsQuery.data)) {
      return [[], []];
    }
    return _.partition(bindingsQuery.data, (binding) => _.includes(binding['@id'], '_default'));
  }, [bindingsQuery.data]);

  const bindingStepDisable = useMemo(() => {
    return _.isEmpty(targetBindings);
  }, [targetBindings]);


  const steps = useMemo(() => {
    const start = [
      {
        // TODO translation
        name: 'Target Model',
        component: NewModelStep,
        isDisabled: modelId != null
      }
    ];

    const base = [
      {
        name: Tl.common.assets,
        component: SelectAssetsStep,
        isDisabled: assetId != null
      },
      {
        name: Tl.common.bindings,
        component: SelectBindingStep,
        isDisabled: bindingStepDisable
      },
      {
        name: Tl.admin.assetwizard.review,
        component: MigrationReviewStep
      }];

    if (modelId != null) {
      return base;
    }
    return [...start, ...base];
  }, [assetId, bindingStepDisable, modelId]);


  const componentProps = useMemo(() => {
    return {
      models,
      assets,
      targetBindings,
      migrationInfo,
      setMigrationInfo
    };
  }, [models, assets, targetBindings, migrationInfo]);

  const [migrateAssets, isMigrating] = useApiCall({
    apiCall: api.core.migrateAssetModel,
    onSuccess: useCallback((migrationReport) => {
      addSuccessAlert(format(T.migrate.success, _.size(migrationReport.assetResults)));
    }, [T.migrate.success, addSuccessAlert]),
    onError: useCallback((error) => {
      addErrorAlert(error);
    }, [addErrorAlert])
  });


  // console.log('migrationInfo', migrationInfo);
  const onMigrate = useCallback(() => {
    // migrate assets with default bindings, i.e. providerClient to Asset bindings
    const mr = migrationRequest(migrationInfo?.targetModel?.id, migrationInfo.targetAssetIds, null);
    migrateAssets(mr);
    return;
    // migrate assets to asset bindings
    // const bindingMap = _.merge({}, ..._.map(migrationInfo.bindingMap, (binding, key) => (
    //   { [key]: binding.id }
    // )));
    // if (!_.isEmpty(migrationInfo.currentBindingIds))
    // migrateAssets(mr);
  }, [migrateAssets, migrationInfo.targetAssetIds, migrationInfo?.targetModel?.id]);

  return (
    <>
      { alerts }
      <ContentPage breadcrumb={breadcrumbs}>
        <GlazeWizard
          stepsContainerClassName={styles.stepsContainer}
          steps={steps}
          onAction={onMigrate}
          onClose={() => history.goBack()}
          isActionExecuting={isMigrating}
          componentProps={componentProps}
          onError={addErrorAlert}
          actionButtonLabel={T.common.migrate}
        />
      </ContentPage>
    </>
  );
};
export default MigrateModel;
